import { DataTable } from '@peritasai/ui-components';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import type { UntrackedAssetCatalogEntity } from '@peritasai/repository';
import { useTranslation } from 'react-i18next';
import { renderTableCell } from '../shared/renderTableCell';

const getColumns = (
  t: (key: string) => string
): GridColDef<UntrackedAssetCatalogEntity>[] => [
  {
    field: 'name',
    headerName: t('pages.entityManagement.untrackedAssetCatalogTable.columnName'),
    flex: 2,
    renderCell: (params) => renderTableCell(params.value, true),
  },
  {
    field: 'manufacturer',
    headerName: t('pages.entityManagement.untrackedAssetCatalogTable.columnManufacturer'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'productNumber',
    headerName: t('pages.entityManagement.untrackedAssetCatalogTable.columnProductNumber'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'type',
    headerName: t('pages.entityManagement.untrackedAssetCatalogTable.columnType'),
    flex: 1,
    renderCell: (params) =>
      params.value != null
        ? renderTableCell(t(`pages.entityManagement.untrackedAssetCatalog.type.${params.value as string}`))
        : renderTableCell(null),
  },
  {
    field: 'description',
    headerName: t('pages.entityManagement.untrackedAssetCatalogTable.columnDescription'),
    flex: 2,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'isActive',
    headerName: t('pages.entityManagement.untrackedAssetCatalogTable.columnActive'),
    flex: 1,
    headerClassName: 'last-column-header',
    renderCell: (params) =>
      renderTableCell(
        params.value === true
          ? t('pages.entityManagement.untrackedAssetCatalogTable.activeYes')
          : t('pages.entityManagement.untrackedAssetCatalogTable.activeNo')
      ),
  },
];

interface UntrackedAssetCatalogTableProps {
  rows: UntrackedAssetCatalogEntity[];
  rowCount: number;
  isLoading: boolean;
  hasSearch: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: UntrackedAssetCatalogEntity) => void;
}

export const UntrackedAssetCatalogTable = ({
  rows,
  rowCount,
  isLoading,
  hasSearch,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
}: UntrackedAssetCatalogTableProps) => {
  const { t } = useTranslation(['periverse']);
  const columns = getColumns(t);

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={rowCount}
      isLoading={isLoading}
      hasSearch={hasSearch}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={onRowClick}
      emptyMessage={t('pages.entityManagement.table.noRows', {
        entityName: t('pages.entityManagement.items.untrackedAssetCatalog'),
      })}
      skeletonColumnCount={6}
    />
  );
};
